import React, { useEffect, useState } from 'react';
import axios from '../../utils/axiosInstance';
import { Link } from 'react-router-dom';
import { FaEdit, FaTrash, FaEye, FaFilter, FaFileImport, FaFileExport, FaExchangeAlt } from 'react-icons/fa';
import CommonTransferForm from '../../components/commonComponent/CommonTransferForm';
import ChapterSelectionModal from '../../components/commonComponent/ChapterSelectionModal';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import UserFilters from '../../components/commonComponent/UserFilters';

const UserIndex = () => {
  // State for modals
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [chapterSelectionModalVisible, setChapterSelectionModalVisible] = useState(false);
  const [transferUser, setTransferUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showColumnFilters, setShowColumnFilters] = useState(false);

  const [filters, setFilters] = useState({
    cluster: '',
    role: '',
    responsibility: '',
    chapter: '',
  });

  const [dropdowns, setDropdowns] = useState({
    clusters: [],
    roles: [],
    responsibilities: [],
    chapters: []
  });

  const getInitialLimit = () => {
    const stored = localStorage.getItem('user_page_limit');
    return stored ? parseInt(stored) : 10;
  };
  const [limit, setLimit] = useState(getInitialLimit());

  const allColumns = [
    { key: 'memberId', label: 'Member ID' },
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'email', label: 'Email' },
    { key: 'role', label: 'Role' },
    { key: 'cluster', label: 'Cluster' },
    { key: 'chapter', label: 'Chapter' },
    { key: 'city', label: 'City' },
    { key: 'country', label: 'Country' },
    { key: 'state', label: 'State' },
    { key: 'businessName', label: 'Business Name' },
    { key: 'businessEmail', label: 'Business Email' },
    { key: 'businessMobileNumber', label: 'Business Contact' },
  ];

  const getInitialVisibleColumns = () => {
    const stored = localStorage.getItem('user_visible_columns');
    return stored ? JSON.parse(stored) : ['memberId', 'firstName', 'lastName', 'email', 'role', 'cluster', 'chapter', 'city'];
  };
  const [visibleColumns, setVisibleColumns] = useState(getInitialVisibleColumns);
  const [columnDropdownOpen, setColumnDropdownOpen] = useState(true);

  useEffect(() => {
    fetchDropdownOptions();
  }, []);

  useEffect(() => {
    localStorage.setItem('user_page_limit', limit);
  }, [limit]);

  useEffect(() => {
    localStorage.setItem('user_visible_columns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    fetchUsers(currentPage, searchQuery, filters);
  }, [currentPage, searchQuery, filters, limit]);

  const fetchDropdownOptions = async () => {
    try {
      const [rolesRes, clustersRes, chaptersRes, responsibilitiesRes] = await Promise.all([
        axios.get('/admin/api/role'),
        axios.get('/admin/api/clusters/all'),
        axios.get('/admin/api/chapters/all'),
        axios.get('/admin/api/responsibility/all'),
      ]);

      setDropdowns({
        roles: rolesRes.data?.roles?.map(r => ({ value: r._id, label: r.name })) || [],
        clusters: clustersRes.data?.cluster?.map(c => ({ value: c._id, label: c.name })) || [],
        chapters: chaptersRes.data?.chapter?.map(ch => ({ value: ch._id, label: ch.name })) || [],
        responsibilities: responsibilitiesRes.data?.responsibility?.map(res => ({ value: res._id, label: res.name })) || [],
      });
    } catch (err) {
      toastError('Failed to load filter dropdowns');
    }
  };

  const fetchUsers = async (page = 1, search = '', filtersData = filters) => {
    try {
      const params = new URLSearchParams({
        userType: 'Member',
        page,
        limit,
      });

      // Only add search parameter if it has a value
      if (search && search.trim()) {
        params.append('search', search.trim());
      }

      // Only add filter parameters if they have values
      if (filtersData.cluster) {
        params.append('clusterId', filtersData.cluster);
      }
      if (filtersData.role) {
        params.append('roleId', filtersData.role);
      }
      if (filtersData.responsibility) {
        params.append('responsibilityId', filtersData.responsibility);
      }
      if (filtersData.chapter) {
        params.append('chapterId', filtersData.chapter);
      }

      const res = await axios.get(`/api/user?${params.toString()}`);
      const { users = [], totalCount = 0 } = res.data;
      setUsers(users);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      toastError('Failed to fetch users');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/user/${id}`);
      toastSuccess('User deleted successfully!');
      setCurrentPage(1);
      fetchUsers(1, searchQuery, filters);
    } catch (err) {
      toastError('Failed to delete user');
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      cluster: '',
      role: '',
      responsibility: '',
      chapter: '',
    });
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleOpenChapterSelection = () => {
    setTransferModalVisible(false);
    setChapterSelectionModalVisible(true);
  };

  const handleChapterTransfer = async (selectedChapterIds) => {
    try {
      if (!transferUser || selectedChapterIds.length === 0) {
        toastError('Please select at least one chapter');
        return;
      }
      console.log("selectedChapterIds",selectedChapterIds);
      // API call to transfer user to selected chapters
      const transferData = {
        user: transferUser._id,
        newchapter: selectedChapterIds.join(', ')
      };

      await axios.post('/api/transfer', transferData);
      toastSuccess('User transferred successfully!');
      
      // Refresh the user list
      fetchUsers(currentPage, searchQuery, filters);
      
      // Close modals and reset state
      setChapterSelectionModalVisible(false);
      setTransferUser(null);
    } catch (err) {
      toastError('Failed to transfer user');
      console.error('Transfer error:', err);
    }
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0 text-dark"> Users</h3>
      </div>

        <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ width: 200 }}
            placeholder="Search User..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
          
          <button
            className="btn btn-sm d-flex align-items-center gap-1"
            style={{
              backgroundColor: showFilters ? '#C44C23' : 'transparent',
              color: showFilters ? '#fff' : '#C44C23',
              border: '1px solid #C44C23',
            }}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter />
          </button>

          <button
            className="btn btn-sm d-flex align-items-center gap-1"
            style={{
              backgroundColor: '#C44C23',
              color: '#fff',
              border: '1px solid #C44C23',
            }}
          >
            <FaFileExport /> Export
          </button>

          <label className="btn btn-sm d-flex align-items-center gap-1 mb-0"
            style={{
              backgroundColor: '#C44C23',
              color: '#fff',
              border: '1px solid #C44C23',
              cursor: 'pointer'
            }}>
            <FaFileImport /> Import
            <input type="file" accept=".csv, .xlsx" style={{ display: 'none' }} />
          </label>

          <button
            className="btn btn-sm d-flex align-items-center gap-1"
            style={{
              backgroundColor: showColumnFilters ? '#C44C23' : 'transparent',
              color: showColumnFilters ? '#fff' : '#C44C23',
              border: '1px solid #C44C23',
            }}
            onClick={() => setShowColumnFilters(!showColumnFilters)}
          >
            <FaEye />
          </button>
        </div>

      {showFilters && (
        <UserFilters
          filters={filters}
          dropdowns={dropdowns}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
        />
      )}

      {showColumnFilters && (
        <div className="mb-2 text-end position-relative" style={{ minWidth: 220 }}>
          <div
            className="dropdown-menu show p-3"
            style={{
              maxHeight: 250,
              overflowY: 'auto',
              right: 0,
              left: 'auto',
              position: 'absolute',
              zIndex: 1050,
              width: 'max-content',
              minWidth: 220,
            }}
          >
            {allColumns.map(col => (
              <div key={col.key} className="form-check mb-1">
                <input
                  type="checkbox"
                  id={`col-filter-${col.key}`}
                  className="form-check-input"
                  checked={visibleColumns.includes(col.key)}
                  onChange={e => {
                    if (e.target.checked) {
                      setVisibleColumns(prev => [...prev, col.key]);
                    } else {
                      setVisibleColumns(prev => prev.filter(k => k !== col.key));
                    }
                  }}
                />
                <label htmlFor={`col-filter-${col.key}`} className="form-check-label" style={{ fontSize: '13px' }}>
                  {col.label}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0 text-dark">All Users</h5>
          <Link to="/user/create" className="btn btn-sm" style={{ backgroundColor: '#C44C23', color: '#fff' }}>
            + Add New User
          </Link>
        </div>

        <div className="card-body p-0">
          <table className="table table-bordered table-sm mb-0" style={{ fontSize: '14px', tableLayout: 'auto', width: '100%' }}>
            <thead className="table-light">
              <tr>
                <th className="py-1">Sr no.</th>
                {visibleColumns.map(key => {
                  const col = allColumns.find(c => c.key === key);
                  return <th key={key} className="py-1">{col?.label}</th>;
                })}
                <th className="py-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? (
                users.map((u, idx) => (
                  <tr key={u._id}>
                    <td>{(currentPage - 1) * limit + idx + 1}</td>
                    {visibleColumns.map(key => {
                      let value = '';
                      if (key === 'memberId') {
                        value = u.memberId;
                      }
                      else if (key === 'firstName') {
                        value = u.firstName;
                        return (
                          <td key={key}>
                            <Link
                              to={`/user/view/${u._id}`}
                              style={{ color: '#C44C23', textDecoration: 'underline', cursor: 'pointer' }}
                            >
                              {value || '-'}
                            </Link>
                          </td>
                        );
                      }
                      else if (key === 'lastName') value = u.lastName;
                      else if (key === 'email') value = u.email;
                      else if (key === 'role') value = u.roles?.[0]?.name;
                      else if (key === 'cluster') value = u.cluster?.[0]?.name;
                      else if (key === 'chapter') value = u.chapter?.[0]?.name;
                      else if (key === 'city') value = u.city?.name;
                      else if (key === 'state') value = u.state?.name;
                      else if (key === 'country') value = u.country?.name;
                      else if (key === 'businessName') value = u.businessName;
                      else if (key === 'businessEmail') value = u.businessEmail;
                      else if (key === 'businessMobileNumber') value = u.businessMobileNumber;
                      return <td key={key}>{value || '-'}</td>;
                    })}
                    <td>
                      {/* <Link to={`/user/edit/${u._id}`} className="btn py-0"
                      style={{
                          fontSize: '12px',
                          lineHeight: '1',
                          boxShadow: 'none',
                          color: '#C44C23',
                          border: 'none'
                        }}><FaEdit /></Link> */}
                        <button
                          type="button"
                          className="btn py-0"
                          style={{
                            fontSize: '12px',
                            lineHeight: '1',
                            boxShadow: 'none',
                            color: '#C44C23',
                            border: 'none'
                          }}
                          onClick={() => {
                            setTransferUser(u);
                            setTransferModalVisible(true);
                          }}
                        >
                          <FaExchangeAlt />
                        </button>
      {/* CommonTransferForm Modal */}
      <CommonTransferForm
        visible={transferModalVisible}
        onCancel={() => {
          setTransferModalVisible(false);
          setTransferUser(null);
        }}
        onSubmit={handleOpenChapterSelection}
        selectedUser={transferUser}
      />

      {/* Chapter Selection Modal */}
      <ChapterSelectionModal
        visible={chapterSelectionModalVisible}
        onCancel={() => {
          setChapterSelectionModalVisible(false);
          setTransferUser(null);
        }}
        onConfirm={handleChapterTransfer}
        chapters={dropdowns.chapters}
        selectedUser={transferUser}
      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={visibleColumns.length + 2} className="text-center">No users found.</td>
                </tr>
              )}
            </tbody>
          </table>

           {/* Control Row */}
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 pb-2 pt-2">
            <div>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto' }}
                value={limit}
                onChange={e => {
                  setLimit(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[10, 25, 50, 100].map(val => (
                  <option key={val} value={val}>{val}</option>
                ))}
              </select>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>

              {(() => {
                const pages = [];
                const maxVisiblePages = 3;
                
                // Always show first page
                pages.push(
                  <button
                    key={1}
                    className={`btn btn-sm page-btn ${currentPage === 1 ? 'active' : ''}`}
                    onClick={() => setCurrentPage(1)}
                  >
                    1
                  </button>
                );

                // Show ellipsis if needed before current page range
                if (currentPage > maxVisiblePages + 1) {
                  pages.push(
                    <span key="ellipsis1" className="px-2">
                      ...
                    </span>
                  );
                }

                // Show pages around current page
                const startPage = Math.max(2, currentPage - 1);
                const endPage = Math.min(totalPages - 1, currentPage + 1);

                for (let page = startPage; page <= endPage; page++) {
                  if (page !== 1 && page !== totalPages) {
                    pages.push(
                      <button
                        key={page}
                        className={`btn btn-sm page-btn ${currentPage === page ? 'active' : ''}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    );
                  }
                }

                // Show ellipsis if needed after current page range
                if (currentPage < totalPages - maxVisiblePages) {
                  pages.push(
                    <span key="ellipsis2" className="px-2">
                      ...
                    </span>
                  );
                }

                // Always show last page if there's more than one page
                if (totalPages > 1) {
                  pages.push(
                    <button
                      key={totalPages}
                      className={`btn btn-sm page-btn ${currentPage === totalPages ? 'active' : ''}`}
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  );
                }

                return pages;
              })()}

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserIndex;
